import {CoworkBarePanel} from '../../../components/admin/CoworkFormPanel';
import { createPortal } from 'react-dom';
import './solicitudReservaPOS.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, KeyRound, Search, ShieldCheck, X } from 'lucide-react';
import {
  buscarSolicitudReserva,
  oficializarSolicitudReserva,
  SolicitudReserva,
} from '../../../services/solicitudReservaService.ts';
import { fetchSesionesAbiertasPorUsuario, SesionPOSLookup } from '../../../services/pos/sesionPOSService.ts';
import { fetchClientes, Cliente } from '../../../services/clienteService.ts';

interface Props {
  open: boolean;
  onClose: () => void;
  onOfficialized: () => void;
  preferredSessionId?: number | null;
  /** Grant del operador identificado por PIN en el navbar; sin él no se oficializa. */
  operadorToken?: string | null;
}

const NO_SESSION_ERROR = 'Debes abrir una sesión POS habilitada para citas antes de registrar solicitudes.';
const NO_OPERATOR_HINT = 'Identifica al operador de caja con su PIN (botón Operador del navbar) antes de registrar la reserva.';
const FOCUSABLE = 'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';


const SolicitudReservaPOSModal: React.FC<Props> = ({ open, onClose, onOfficialized, preferredSessionId = null, operadorToken = null }) => {
  const [codigo, setCodigo] = useState('');
  const [solicitud, setSolicitud] = useState<SolicitudReserva | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteId, setClienteId] = useState<number | null>(null);
  // Sesión POS EXPLÍCITA del operador: el backend no infiere ninguna caja, así
  // que sin sesión abierta no se busca ni se oficializa nada.
  const [sesionesPos, setSesionesPos] = useState<SesionPOSLookup[]>([]);
  const [sesionPosId, setSesionPosId] = useState<number | null>(null);
  const [sesionLoading, setSesionLoading] = useState(false);

  // Identidad inmutable de cada operación: sesión POS + código normalizado.
  // Cualquier cambio de código, de sesión, cierre o desmontaje incrementa la
  // generación y espeja el valor vigente en refs; toda continuación (después
  // de CADA await y antes de cualquier callback) compara ambas cosas.
  const operationGeneration = useRef(0);
  const mountedRef = useRef(true);
  const codigoRef = useRef('');
  const sesionRef = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const operatorTokenRef = useRef<string | null>(operadorToken);
  operatorTokenRef.current = operadorToken;
  const currentIdentity = useCallback(
    () => `${sesionRef.current ?? ''}|${codigoRef.current}|${operatorTokenRef.current ?? ''}`,
    [],
  );
  const invalidate = useCallback(() => { operationGeneration.current += 1; }, []);
  const isCurrent = useCallback(
    (generation: number, identity: string) =>
      mountedRef.current && generation === operationGeneration.current && identity === currentIdentity(),
    [currentIdentity],
  );

  const handleClose = useCallback(() => {
    // Invalidar SIN esperar al re-render del padre: ninguna respuesta pendiente
    // puede mostrar datos, oficializar ni disparar callbacks tras cerrar. El
    // estado transitorio se limpia aquí mismo (el padre puede tardar en cerrar).
    invalidate();
    setLoading(false);
    setSolicitud(null);
    setClienteId(null);
    setError('');
    onCloseRef.current();
  }, [invalidate]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationGeneration.current += 1;
    };
  }, []);

  useEffect(() => {
    invalidate();
    if (!open) {
      setCodigo(''); setSolicitud(null); setError(''); setDone(false); setLoading(false);
      setSesionesPos([]); setSesionPosId(null); setSesionLoading(false);
      codigoRef.current = ''; sesionRef.current = null;
      return;
    }
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const generation = operationGeneration.current;
    let cancelled = false;
    const live = () => !cancelled && mountedRef.current && generation === operationGeneration.current;
    setSesionLoading(true);
    fetchSesionesAbiertasPorUsuario()
      .then((sesiones: SesionPOSLookup[]) => {
        if (!live()) return;
        const abiertas = (sesiones || []).filter((sesion: SesionPOSLookup) => sesion.estado === 'abierta');
        const operables = preferredSessionId
          ? abiertas.filter(sesion => sesion.id === preferredSessionId)
          : abiertas;
        const selected = operables.length === 1 ? operables[0].id : null;
        sesionRef.current = selected;
        setSesionesPos(operables);
        setSesionPosId(selected);
        if (operables.length === 0) setError(NO_SESSION_ERROR);
      })
      .catch(() => {
        if (!live()) return;
        sesionRef.current = null;
        setSesionesPos([]); setSesionPosId(null); setError(NO_SESSION_ERROR);
      })
      .finally(() => { if (live()) setSesionLoading(false); });
    return () => {
      cancelled = true;
      operationGeneration.current += 1;
      const previous = previousFocusRef.current;
      if (previous && previous.isConnected && typeof previous.focus === 'function') previous.focus();
    };
  }, [open, invalidate, preferredSessionId]);

  // Foco inicial en el código, Escape cierra y Tab queda atrapado en el diálogo.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      (inputRef.current || dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE) || dialogRef.current)?.focus();
    }, 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!focusable.length) { event.preventDefault(); dialogRef.current.focus(); return; }
      const first = focusable[0], last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!dialogRef.current.contains(active)) { event.preventDefault(); first.focus(); return; }
      if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, handleClose]);

  if (!open) return null;
  const normalized = codigo.trim().toUpperCase();
  const canOperate = sesionPosId !== null && !sesionLoading;

  const onCodigoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value.toUpperCase();
    codigoRef.current = next.trim();
    invalidate();
    setCodigo(next);
    setSolicitud(null);
    setClienteId(null);
    setError('');
    setLoading(false);
  };

  const onSesionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value ? Number(event.target.value) : null;
    sesionRef.current = next;
    invalidate();
    setSesionPosId(next);
    setSolicitud(null);
    setClienteId(null);
    setError('');
    setLoading(false);
  };

  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!normalized || sesionPosId === null || sesionLoading) return;
    const generation = ++operationGeneration.current;
    const identity = currentIdentity();
    const operatingSessionId = sesionPosId;
    setLoading(true); setError(''); setSolicitud(null);
    try {
      const result = await buscarSolicitudReserva(normalized, operatingSessionId);
      if (!isCurrent(generation, identity)) return;
      setSolicitud(result);
      setClienteId(null);
      if (result.cliente === null && result.contacto_cotizacion) {
        const registered = await fetchClientes();
        if (!isCurrent(generation, identity)) return;
        setClientes(registered.filter(cliente => cliente.usuario));
      }
    } catch (err: any) {
      if (!isCurrent(generation, identity)) return;
      const data = err?.response?.data;
      setError(data?.detail || data?.sesion_pos_id || data?.codigo || 'No se encontró una solicitud válida en esta sucursal.');
    } finally {
      if (isCurrent(generation, identity)) setLoading(false);
    }
  };

  const officialize = async () => {
    if (!solicitud || loading || sesionPosId === null || sesionLoading || !operadorToken) return;
    const generation = ++operationGeneration.current;
    const identity = currentIdentity();
    const operatingSessionId = sesionPosId;
    const operatingToken = operadorToken;
    setLoading(true); setError('');
    try {
      if (clienteId !== null) {
        await oficializarSolicitudReserva(solicitud.codigo, operatingSessionId, operatingToken, clienteId);
      } else {
        await oficializarSolicitudReserva(solicitud.codigo, operatingSessionId, operatingToken);
      }
      if (!isCurrent(generation, identity)) return;
      setDone(true);
      if (!isCurrent(generation, identity)) return;
      onOfficialized();
    } catch (err: any) {
      if (!isCurrent(generation, identity)) return;
      const data = err?.response?.data;
      setError(data?.detail || data?.cliente_id || data?.sesion_pos_id || data?.espacio_servicio || data?.codigo || 'No se pudo registrar la reserva.');
    } finally {
      if (isCurrent(generation, identity)) setLoading(false);
    }
  };

  const selectedSession = sesionesPos.find(sesion => sesion.id === sesionPosId) ?? null;
  const sesionLabel = sesionLoading
    ? 'Verificando la sesión POS…'
    : selectedSession
      ? `Sesión POS ${selectedSession.codigo || `#${selectedSession.id}`}`
      : sesionesPos.length > 1
        ? 'Selecciona la sesión POS desde la que operas'
      : 'Sin sesión POS abierta';

  return createPortal(
    <div className="srp-overlay" onMouseDown={event => { if (event.target === event.currentTarget) handleClose(); }}>
      <CoworkBarePanel ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="solicitud-pos-title" aria-describedby="solicitud-pos-desc" tabIndex={-1} className="srp-card">
        <header className="srp-head">
          <div className="srp-head-main">
            <span className="srp-head-icon"><ShieldCheck size={24}/></span>
            <div><p className="srp-eyebrow">RESERVAS · PUNTO DE VENTA</p><h2 id="solicitud-pos-title">Registrar solicitud desde POS</h2><p id="solicitud-pos-desc">Encuentra la solicitud del cliente y confirma su reserva.</p></div>
          </div>
          <button type="button" className="srp-close" aria-label="Cerrar" onClick={handleClose}><X size={18}/></button>
        </header>
        <div className="srp-session"><span className={`srp-session-dot ${selectedSession ? 'active' : ''}`}/><div><small>CAJA DE REGISTRO</small><p aria-live="polite">{sesionLabel}</p></div>{selectedSession?.sucursal_nombre && <span className="srp-branch">{selectedSession.sucursal_nombre}</span>}</div>
        <div className="srp-body">
          {done ? <div className="srp-done"><span><CheckCircle2 size={32}/></span><h3>Reserva oficial registrada</h3><p>Ya está vinculada a caja y puede continuar con el flujo normal.</p><button type="button" className="srp-primary" onClick={handleClose}>Continuar</button></div> : <>
            <div className="srp-progress" aria-label="Pasos del registro"><span className="active"><b>1</b>Buscar código</span><span className={solicitud ? 'active' : ''}><b>2</b>Revisar y registrar</span></div>
            {sesionesPos.length > 1 && <div className="srp-field"><label htmlFor="solicitud-pos-session">Sesión POS</label><select id="solicitud-pos-session" className="srp-select" value={sesionPosId ?? ''} onChange={onSesionChange}><option value="">Selecciona una sesión</option>{sesionesPos.map(sesion => <option key={sesion.id} value={sesion.id}>{sesion.codigo || `Sesión #${sesion.id}`}{sesion.sucursal_nombre ? ` · ${sesion.sucursal_nombre}` : ''}</option>)}</select></div>}
            <form onSubmit={search}>
              <label className="srp-label" htmlFor="solicitud-code">Código enviado por WhatsApp</label>
              <div className="srp-row"><div className="srp-input-wrap"><KeyRound size={18}/><input ref={inputRef} id="solicitud-code" className="srp-input" value={codigo} onChange={onCodigoChange} placeholder="SOL-XXXXXXXX" maxLength={20} autoComplete="off" inputMode="text"/></div><button type="submit" className="srp-search" disabled={!normalized || loading || !canOperate}><Search size={17}/>{loading ? 'Buscando…' : 'Buscar'}</button></div>
              <p className="srp-hint">Ingresa el código compartido por el cliente para consultar sus datos.</p>
            </form>
            {error && <div role="alert" className="srp-error">{error}</div>}
            {solicitud ? <section className="srp-result">
              <div className="srp-result-head"><span className="srp-result-icon"><CheckCircle2 size={19}/></span><div><small>SOLICITUD ENCONTRADA</small><strong>{solicitud.cliente_nombre}</strong></div><span className="srp-code">{solicitud.codigo}</span></div>
              <div className="srp-result-grid"><div><span>Servicio / espacio</span><strong>{solicitud.servicio_nombre}</strong>{solicitud.espacio_nombre && <small>{solicitud.espacio_nombre}</small>}</div><div><span>Fecha y hora</span><strong>{solicitud.fecha_inicio}</strong><small>{solicitud.hora_inicio?.slice(0,5)}{solicitud.hora_fin ? ` – ${solicitud.hora_fin.slice(0,5)}` : ''}</small></div></div>
              <p className="srp-hint">Al registrar, se verificará la disponibilidad y la reserva quedará vinculada a esta sesión POS.</p>
              {solicitud.contacto_cotizacion && <div className="srp-field">
                <p className="srp-hint">{solicitud.contacto_cotizacion.email} · {solicitud.contacto_cotizacion.telefono}{solicitud.contacto_cotizacion.ci_nit ? ` · CI/NIT: ${solicitud.contacto_cotizacion.ci_nit}` : ''}</p>
                {solicitud.contacto_cotizacion.observaciones && <p className="srp-hint">{solicitud.contacto_cotizacion.observaciones}</p>}
                {solicitud.cliente === null && <>
                  <label htmlFor="solicitud-pos-cliente">Cliente de la reserva</label>
                  <select id="solicitud-pos-cliente" className="srp-select" value={clienteId ?? ''} disabled={loading} onChange={event => setClienteId(event.target.value ? Number(event.target.value) : null)}>
                    <option value="">Registrar nuevo cliente con estos datos</option>
                    {clientes.map(cliente => <option key={cliente.id} value={cliente.usuario!.id}>{`${cliente.usuario!.first_name} ${cliente.usuario!.last_name}`.trim()} · {cliente.usuario!.email}</option>)}
                  </select>
                  <p className="srp-hint">Verifica los datos del visitante. Si ya es cliente, selecciónalo antes de registrar.</p>
                </>}
              </div>}
              {!operadorToken && <p className="srp-operator">{NO_OPERATOR_HINT}</p>}
              <button type="button" className="srp-primary" onClick={officialize} disabled={loading || !canOperate || !operadorToken}><ShieldCheck size={17}/>{loading ? 'Registrando…' : 'Registrar reserva oficial'}</button>
            </section> : <div className="srp-empty"><ShieldCheck size={19}/><p>La solicitud se validará con la disponibilidad y las condiciones de la reserva antes de registrarla.</p></div>}
          </>}
        </div>
        <footer className="srp-footer"><span><KeyRound size={13}/>Registro autorizado desde caja</span><button type="button" onClick={handleClose}>Volver al POS</button></footer>
      </CoworkBarePanel>
    </div>, document.body,
  );
};
export default SolicitudReservaPOSModal;

